import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import CustomPagination from "../../Components/CustomPagination";
import PageTitle from "../../Components/UI/PageTitle";
import RowLoader from "../../Components/UI/RowLoader";
import { getPartnerDownliners, getPartnerUpliners } from "../../apis";
import Tabs from "../../Components/Tabs";
import { transformKeys } from "../../_helper";
import axios from "axios";

const TABS_DATA = [
  { label: "Upliners", key: "upliners" },
  { label: "Downliners", key: "downliners" },
];
const PartnerNetwork = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isExportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [currentTab, setCurrentTab] = useState(
    new URLSearchParams(location.search).get("tab") || "upliners"
  );

  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );

  useEffect(() => {
    setLoading(true);
    const endpoint =
      currentTab === "downliners" ? getPartnerDownliners : getPartnerUpliners;

    endpoint(searchTerm, id, currentPage)
      .then((response) => {
        const handleData = response.data.data.map((d) => transformKeys(d));
        setData(handleData);
        setPaginator(response.data.paginator);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentTab, searchTerm, currentPage, id]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("tab", currentTab);
    if (searchTerm) params.set("search", searchTerm);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [searchTerm, currentPage, sortKey, sortDirection, navigate, currentTab]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const currentEndpoint =
        currentTab === "downliners" ? "clientDownliners" : "clientUpliners";
      const { data } = await axios.get(
        `/admin/partners-list/${currentEndpoint}/80/excelExport`,
        {
          params: {
            search: searchTerm,
          },
          responseType: "blob",
        }
      );

      const fileName = `Partner_Netwrok_${currentTab}_${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "")}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleTabClick = (key) => {
    setCurrentTab(key);
    setCurrentPage(1); // Reset to first page on tab change
  };

  const handleFilter = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column, direction) => {
    setSortKey(column.sortKey);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page on sort change
  };
  const hiddenColumns = ["client_id"]; // Add column keys you want to hide

  const columns = useMemo(() => {
    return createColumns(data, [], hiddenColumns, true).map((col) => {
      const commonProps = {
        ...col,
        sortable: false,
        width: col.name === "Num" ? "70px" : "1fr",
      };

      switch (col.name) {
        case "Client Profile Pic":
          return {
            ...commonProps,
            cell: (row) => (
              <img
                src={row.client_profile_pic}
                alt="Client"
                width={50}
                height={50}
                style={{
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ),
          };

        case "Is Active":
          return {
            ...commonProps,
            cell: (row) => (row.is_active == 1 ? "Yes" : "No"),
          };

        case "Is Pv Active":
          return {
            ...commonProps,
            cell: (row) => (row.is_pv_active == 1 ? "Yes" : "No"),
          };

        default:
          return commonProps;
      }
    });
  }, [data]);

  return (
    <>
      <PageTitle
        BreadcrumbItems={[
          { title: "Home", link: "/" },
          { title: "Partners", link: "/partners-list/" },
          { title: "Partner Network" },
        ]}
        title="Partner Network"
      />

      <div className="bg-white flex flex-col mt-4">
        <Tabs
          options={TABS_DATA}
          onTabClick={handleTabClick}
          currentTab={currentTab}
        />
        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <div className="relative">
            <div className="tw-mb-2">
              <input
                type="text"
                onChange={handleFilter}
                value={searchTerm}
                className="border border-blue-300 rounded-lg p-2 col-md-12 tw-mb-2"
                placeholder="Search by name"
              />
            </div>
          </div>
          <DataTable
            columns={columns}
            data={data}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={loading}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />
          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={paginator?.last_page}
              currentPage={paginator?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      {data?.length > 0 && (
        <button
          className={`tw-border tw-bg-green-600/20 tw-flex tw-justify-center tw-w-[200px] tw-h-10 tw-text-center tw-border-green-500 tw-px-4 tw-py-2 tw-rounded-lg tw-items-center tw-gap-2 tw-transition-all hover:tw-bg-green-600/30`}
          onClick={handleExport}
        >
          {isExportLoading ? (
            <div className="tw-flex tw-justify-center">
              <RowLoader paddingStyle="tw-py-0" />
            </div>
          ) : (
            <>
              <img
                src="/imgs/excelIcon.png"
                className="tw-size-5"
                alt="Export"
              />
              <span>Export as excel file</span>
            </>
          )}
        </button>
      )}
    </>
  );
};

export default PartnerNetwork;
